import { ErrorMessage } from '@/common/atoms/ErrorMessage'
import { Loading } from '@/common/atoms/Loading'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  GetEstateVisitsQuery,
  useGetEstateVisitsQuery,
  useCreateVisitMutation,
} from '@gql'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from '@/components/ui/use-toast'
import { EstateVisitsList } from './EstateVisitsList'
import { EstateVisitCreateButton } from './EstateVisitCreateButton'
import useCurrentSeller from '@/seller/hooks/useCurrentSeller'

type EstateVisitsModalProps = {
  open: boolean
  setOpenModal: (open: boolean) => void
  estateId: string
  estateVisitSlotDuration: number
  disabled?: boolean
}

export const EstateVisitsModal = ({
  open,
  setOpenModal,
  estateId,
  estateVisitSlotDuration,
  disabled,
}: EstateVisitsModalProps) => {
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState<string>('')

  // Sellers can't update visits, only admins can
  const seller = useCurrentSeller()
  const readOnly = seller != null

  const {
    data: visitsData,
    loading: visitsLoading,
    error: visitsError,
  } = useGetEstateVisitsQuery({
    variables: { id: estateId },
  })

  const [createVisit, { loading: createVisitLoading }] =
    useCreateVisitMutation()

  const visits = useMemo(() => {
    return visitsData?.visit ?? []
  }, [visitsData])

  const daysWithVisits = useMemo(() => {
    const daysMap = new Map<string, GetEstateVisitsQuery['visit']>()

    visits.forEach((visit) => {
      const date = format(new Date(visit.startTime), 'yyyy-MM-dd')

      if (date) {
        const dayKey = date
        if (!daysMap.has(dayKey)) {
          daysMap.set(dayKey, [])
        }
        daysMap.set(dayKey, [...daysMap.get(dayKey)!, visit])
      }
    })

    return daysMap
  }, [visits])

  const sortedDays = useMemo(() => {
    return Array.from(daysWithVisits.keys()).sort(
      (a, b) => new Date(a).getTime() - new Date(b).getTime()
    )
  }, [daysWithVisits])

  // Set initial active tab when data loads or when current tab becomes invalid
  useEffect(() => {
    if (sortedDays.length > 0) {
      // If no active tab is set, or current active tab is no longer valid, set to first available day
      if (!activeTab || !sortedDays.includes(activeTab)) {
        setActiveTab(sortedDays[0])
      }
    }
  }, [sortedDays, activeTab])

  const handleTabChange = (value: string) => {
    setActiveTab(value)
  }

  const handleCreateVisit = async (
    buyerMemberId: string,
    startTime: Date,
    endTime: Date
  ) => {
    await createVisit({
      variables: {
        estateId,
        buyerId: buyerMemberId,
        startTime: startTime.toISOString(),
        endTime: endTime.toISOString(),
      },
      refetchQueries: ['getEstateVisits'],
      onCompleted: () => {
        toast({
          description: t('EstateVisitCreateButton.toast.success'),
          variant: 'success',
        })
      },
      onError(error: any) {
        console.error(error)
        toast({
          description: t('EstateVisitCreateButton.toast.error'),
          variant: 'destructive',
        })
      },
    })
  }

  const renderDialogContent = () => {
    if (visitsLoading) return <Loading center active />

    if (visitsError)
      return <ErrorMessage>{t('EstateVisitSpanModal.error')}</ErrorMessage>

    if (!visits.length) return <p>{t('EstateVisitSpanModal.noVisits')}</p>

    return (
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="tw-h-auto tw-max-h-[100px] tw-flex-wrap tw-overflow-auto tw-gap-2 tw-bg-transparent tw-justify-stretch">
          {sortedDays.map((day) => (
            <TabsTrigger
              value={day}
              key={day}
              className="tw-border !tw-rounded-full tw-border-zinc data-[state=active]:tw-border-black"
            >
              {format(new Date(day), 'E dd/MM/yyyy', { locale: fr })}
            </TabsTrigger>
          ))}
        </TabsList>
        {sortedDays.map((day) => {
          const dayVisits = daysWithVisits.get(day)!

          return (
            <TabsContent value={day} key={day}>
              <EstateVisitsList
                visits={dayVisits}
                visitSlotDuration={estateVisitSlotDuration}
              />
            </TabsContent>
          )
        })}
      </Tabs>
    )
  }

  return (
    <Dialog open={open} onOpenChange={setOpenModal}>
      <DialogTrigger asChild>
        <Button variant="secondary" disabled={disabled}>
          {t('EstateVisitSpanModal.title')}
        </Button>
      </DialogTrigger>
      <DialogContent className="tw-min-w-[650px]">
        <DialogHeader className="tw-mb-5">
          <div className="tw-flex tw-items-center tw-justify-between">
            <DialogTitle>{t('EstateVisitSpanModal.title')}</DialogTitle>
          </div>
        </DialogHeader>
        {renderDialogContent()}
        {!readOnly && (
          <EstateVisitCreateButton
            enabled={!createVisitLoading}
            visitSlotDuration={estateVisitSlotDuration}
            defaultDate={activeTab}
            onSubmit={handleCreateVisit}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}
